import { Component, HostListener, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';
import _ from 'lodash';
import moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import {
  CollaboratorType,
  Conversation,
  CUSTOM_FIELD_TYPES,
  SimpleConditionsOperator,
  SubStatus,
  Escalation,
} from 'src/models';
import { PROJECT_VISIBILITY } from 'src/models/projects';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CollaboratorTypesService } from 'src/services/collaborator-types.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { EscalationsService } from 'src/services/escalations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService, STATUS_OPTIONS } from 'src/services/searchable-popover.service';
import { ServerEventsService, ServerEventSubscription } from 'src/services/server-events.service';
import { TagsService } from 'src/services/tags.service';
import { UsersService } from 'src/services/users.service';

import { EmojisService } from '../../services/emojis.service';
import { ActionDropdownComponent, ActionDropdownType } from '../components/action-dropdown/action-dropdown.component';
import { ColumnManagerComponent, TableColumn } from '../components/column-manager/column-manager.component';
import { ConversationListComponent } from '../components/conversation-list/conversation-list.component';
import { EditTagLinksComponent } from '../components/edit-tag-links/edit-tag-links.component';
import { TextPopoverComponent } from '../components/text-popover/text-popover.component';
import {
  CONVERSATION_STATUS,
  INBOX_LAYOUT_TYPES,
  INBOX_SORT,
  INBOX_TYPES,
  PRIVATE_CHANNEL_NAME,
  routes,
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  SUPPORT_STEP_ASSIGNEE_TYPES,
} from '../constants';
import {
  FILTER_OPERATORS,
  FilterOperator,
  FilterOption,
  FilterValue,
  reconstructAndHydrateUiFilters,
  SelectedFilterMap,
} from '../utils/filter-utils';
import {
  downloadCSV,
  formatLink,
  formatConversationContent,
  formatSlackContent,
  getDisplayTextForDuration,
  getSlackLink,
  getWhereClausesFromFilters,
  isUrl,
  transformMessage,
} from '../utils/utils';

const FILTER_ASSIGNEE = 'Assignee';
const FILTER_CUSTOMER = 'Customer';
const FILTER_CHANNEL = 'Channel';
const FILTER_STATUS_TYPE = 'Status Type';
const FILTER_STATUS = 'Status';
const FILTER_SOURCE_TYPE = 'Source';
const FILTER_TAG = 'Tag';
const FILTER_CUSTOMER_TAG = 'Customer Tag';
const FILTER_CHANNEL_TAG = 'Channel Tag';
const FILTER_CREATED_AT = 'Date submitted';
const FILTER_TICKET_TYPE = 'Ticket Type';
const FILTER_SUBMITTER = 'Submitter';
const FILTER_PROJECT = 'Project';
const FILTER_OPEN_EXTERNAL_TASKS = 'External Tasks';
const FILTER_ACCOUNT = 'Account';
const FILTER_ACCOUNT_TAG = 'Account Tag';

const STATUS_COLUMN_MAP = {
  [CONVERSATION_STATUS.OPEN]: {
    icon: 'custom-open',
    cssClass: 'needs-response',
    countColor: 'danger',
  },
  [CONVERSATION_STATUS.IN_PROGRESS]: {
    icon: 'custom-in-progress',
    cssClass: 'in-progress',
    countColor: 'warning',
  },
  [CONVERSATION_STATUS.ON_HOLD]: {
    icon: 'custom-on-hold',
    cssClass: 'on-hold',
    countColor: 'dark',
  },
  [CONVERSATION_STATUS.CLOSED]: {
    icon: 'custom-closed',
    cssClass: 'closed',
    countColor: 'success',
  },
};

interface StatusFilterOption {
  key: Statuses;
  label: string;
}
enum Statuses {
  OPEN = 'open',
  IN_PROGRESS = 'in_progress',
  ON_HOLD = 'on_hold',
  CLOSED = 'closed',
  ALL = 'all',
}

@Component({
  selector: 'app-inbox',
  templateUrl: './inbox.component.html',
  styleUrls: ['./inbox.component.scss'],
})
export class InboxComponent implements OnInit, OnDestroy {
  @ViewChildren(ConversationListComponent) convoLists: QueryList<ConversationListComponent>;
  @ViewChild(ConversationListComponent) sidebarConvoList: ConversationListComponent;

  isLoading = false;
  layoutType: 'table' | 'grid' | 'sidebar';

  ColumnMode = ColumnMode;

  selectedConversations: Partial<Conversation>[] = [];
  slackScopeRequired: boolean;

  filters: SelectedFilterMap = new Map();
  routerSub: Subscription;

  limit = 50;
  cursor: any;
  searchText: string;
  sidebarConvoCount: number;
  selectedSidebarConversationId: string;
  selectedSidebarConversationIndex: number = -1;

  tableRowHeight: any = 46;
  tableMessageWidth = 520;
  tableShowId = true;

  emailInboxId: string;
  inAppInboxId: string;
  microsoftTeamsInboxId: string;
  savedFilterId: string;

  subscription: ServerEventSubscription;
  bulkUpdateSubscription: ServerEventSubscription;
  conversationsSubscription: Subscription;
  escalationsSubscription: Subscription;

  dragEnabled = true;
  draggedConversation: Conversation;
  dragHighlightedCol: string;

  selectAllPagesForBulkUpdate = false;
  isOnFirstPage = true;
  isProcessingBulkUpdate = false;
  bulkUpdateCount: number;
  bulkUpdateRunId: string;
  collaboratorTypes: Partial<CollaboratorType>[] = [];
  showingSidebarOptions: boolean;
  subStatuses: SubStatus[] = [];
  subStatusKeyCount: Record<string, number> = {};

  isDownloadingCsv = false;

  // Table column management
  tableColumns: TableColumn[] = [];

  CONVERSATION_STATUS = CONVERSATION_STATUS;
  INBOX_TYPES = INBOX_TYPES;
  PROJECT_VISIBILITY = PROJECT_VISIBILITY;
  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;
  ESCALATION_STATUSES = ESCALATION_STATUSES;
  ESCALATION_STATUS_LABELS = ESCALATION_STATUS_LABELS;

  tabs = [
    {
      key: INBOX_TYPES.ME,
      label: 'Assigned to me',
      filters: [
        {
          field: 'assignedToUserId',
          values: [{ id: this.authService.profile.id, title: this.authService.profile.name }],
        },
      ],
    },
    {
      key: INBOX_TYPES.COLLABORATING_ON,
      label: 'Collaborating on',
    },
    {
      key: INBOX_TYPES.FOLLOWING,
      label: 'Following',
    },
    {
      key: INBOX_TYPES.ALL,
      label: 'All',
      filters: [],
    },
    {
      key: INBOX_TYPES.UNASSIGNED,
      label: 'Unassigned',
      filters: [
        {
          field: 'assignedToUserId',
          values: [{ id: null, title: 'None' }],
        },
      ],
    },
    {
      key: INBOX_TYPES.CLOSED,
      label: 'Closed',
      filters: [
        {
          field: 'status',
          values: [STATUS_OPTIONS[3]],
        },
      ],
    },
    {
      key: INBOX_TYPES.INTAKE,
      label: 'Intake',
      filters: [
        {
          field: 'status',
          values: [STATUS_OPTIONS[4]],
        },
      ],
    },
    {
      key: INBOX_TYPES.ESCALATIONS,
      label: 'Escalations',
      filters: [
        {
          field: 'escalations.status',
          values: [ESCALATION_STATUSES.PENDING],
        },
      ],
    },
  ];
  selectedTab = this.tabs[0];

  statuses = [
    {
      key: 'open',
      label: 'Open',
    },
    {
      key: 'on_hold',
      label: 'On Hold',
    },
    {
      key: 'closed',
      label: 'Closed',
    },
  ];
  statusIndex = 0;

  sortField = 'createdAt';
  sortDesc = true;
  sortLabel = 'Newest';

  filterOptions: FilterOption[] = [
    {
      label: FILTER_ASSIGNEE,
      id: 'assignedToUserId',
      icon: 'person',
      type: 'select',
      searchablePopoverOptions: {
        showCurrentUserOption: true,
      },
    },
    {
      label: this.authService.tenant.accountsEnabled ? FILTER_CHANNEL : FILTER_CUSTOMER,
      id: 'customerId',
      icon: this.authService.tenant.accountsEnabled ? 'chatbubble' : 'business',
      type: 'select',
    },
    {
      label: FILTER_SUBMITTER,
      id: 'submitterUserId',
      icon: 'person',
      type: 'select',
      searchablePopoverOptions: {
        showCurrentUserOption: true,
      },
    },
    {
      label: FILTER_STATUS_TYPE,
      id: 'status',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: FILTER_STATUS,
      id: 'subStatus.key',
      icon: 'pulse',
      type: 'select',
    },
    {
      label: FILTER_SOURCE_TYPE,
      id: 'sourceType',
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
      ],
    },
    {
      label: FILTER_TICKET_TYPE,
      id: 'ticketTypeId',
      icon: 'ticket',
      type: 'select',
    },
    {
      label: FILTER_TAG,
      id: 'tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: this.authService.tenant.accountsEnabled ? FILTER_CHANNEL_TAG : FILTER_CUSTOMER_TAG,
      id: 'customer.tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: 'Priority',
      id: 'priority',
      icon: 'alert-circle',
      type: 'select',
      values: [
        {
          label: 'Low',
          id: '3',
        },
        {
          label: 'Medium',
          id: '5',
        },
        {
          label: 'High',
          id: '7',
        },
        {
          label: 'Critical',
          id: '9',
        },
      ],
    },
    {
      label: 'Custom Field',
      id: 'ticketTypeFields',
      icon: 'flask',
      type: 'variable',
      searchablePopoverOptions: {
        allowedTypes: [
          CUSTOM_FIELD_TYPES.CHECKBOX,
          CUSTOM_FIELD_TYPES.SINGLE_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_SELECT,
          CUSTOM_FIELD_TYPES.DATE,
          CUSTOM_FIELD_TYPES.USER_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_SELECT,
          CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
          CUSTOM_FIELD_TYPES.MULTI_USER_GROUP_SELECT,
        ],
      },
    },
    {
      label: FILTER_CREATED_AT,
      id: 'createdAt',
      icon: 'calendar',
      type: 'date',
    },
    !this.authService.selectedProject
      ? {
          label: FILTER_PROJECT,
          id: 'projectId',
          icon: 'folder-open',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: FILTER_ACCOUNT,
          id: 'accountId',
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: FILTER_ACCOUNT_TAG,
          id: 'account.tags.id',
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    {
      label: FILTER_OPEN_EXTERNAL_TASKS,
      id: 'externalTasks.isClosed',
      icon: 'checkbox',
      type: 'select',
    },
  ].filter((o) => o !== undefined) as FilterOption[];
  isFilteringEscalationsByMe = true;

  kanbanColumns: any[] = [];

  statusFilterOptions: StatusFilterOption[] = [
    { key: Statuses.OPEN, label: 'Open' },
    { key: Statuses.ON_HOLD, label: 'On Hold' },
    { key: Statuses.CLOSED, label: 'Closed' },
    { key: Statuses.ALL, label: 'All' },
  ];

  sortOptions = {
    [INBOX_SORT.CREATED_AT_DESCENDING]: {
      label: 'Newest',
      sortDesc: true,
      sortField: 'createdAt',
      key: INBOX_SORT.CREATED_AT_DESCENDING,
    },
    [INBOX_SORT.CREATED_AT_ASCENDING]: {
      label: 'Oldest',
      sortDesc: false,
      sortField: 'createdAt',
      key: INBOX_SORT.CREATED_AT_ASCENDING,
    },
    [INBOX_SORT.LAST_ACTIVITY_AT_DESCENDING]: {
      label: 'Recent message activity',
      sortDesc: true,
      sortField: 'lastActivityAt',
      key: INBOX_SORT.LAST_ACTIVITY_AT_DESCENDING,
    },
    [INBOX_SORT.UPDATED_AT_DESCENDING]: {
      label: 'Recently updated',
      sortDesc: true,
      sortField: 'updatedAt',
      key: INBOX_SORT.UPDATED_AT_DESCENDING,
    },
    [INBOX_SORT.PRIORITY_ASCENDING]: {
      label: 'High priority',
      sortDesc: true,
      sortField: 'priority',
      key: INBOX_SORT.PRIORITY_ASCENDING,
    },
  };

  select = [
    'id',
    'status',
    'channelId',
    'teamId',
    'communityId',
    'createdAt',
    'updatedAt',
    'lastActivityAt',
    'statusUpdatedAt',
    'friendlyId',
    'wasManuallyCreated',
    'category',
    'closedAt',
    'respondedAt',
    'responseTime',
    'responseTimeWorking',
    'resolutionTime',
    'resolutionTimeWorking',
    'priority',
    'notes',
    'wakeUpAt',
    'snoozedAt',
    'excludeAnalytics',
    'assignedToUserId',
    'initialMessageId',
    'summary',
    'latestTopLevelTs',
    'threadTs',
    'escalateAt',
    'escalationTier',
    'sourceType',
    'submitterUserId',
    'inboundEmailRuleId',
    'widgetId',
    'externalChatConnectionId',
    'title',
    'sentiment',
    'language',
    'customer.id',
    'customer.name',
    'customer.tags.id',
    'customer.tags.name',
    'assignedToUser.id',
    'assignedToUser.name',
    'assignedToUser.photo',
    'tags.id',
    'tags.name',
    'initialMessage.ts',
    'initialMessage.sourceType',
    'initialMessage.nlpResult',
    'initialMessage.id',
    'initialMessage.botId',
    'initialMessage.botName',
    'initialMessage.content',
    'initialMessage.fullContent',
    'initialMessage.sentByUser.id',
    'initialMessage.sentByUser.name',
    'initialMessage.sentByUser.email',
    'initialMessage.sentByUser.isExternal',
    'initialMessage.sentByUser.photo',
    'initialMessage.inbound.id',
    'initialMessage.inbound.inboundEmail.id',
    'initialMessage.inbound.inboundEmail.inbound_email_rule_id',
    'initialMessage.inbound.inboundEmail.fromUser.id',
    'initialMessage.inbound.inboundEmail.fromUser.name',
    'initialMessage.inbound.inboundEmail.fromUser.email',
    'initialMessage.inbound.inboundEmail.fromUser.isExternal',
    'initialMessage.inbound.widgetMessage.id',
    'initialMessage.inbound.widgetMessage.widget_id',
    'initialMessage.inbound.widgetMessage.widgetSession.id',
    'initialMessage.inbound.widgetMessage.widgetSession.name',
    'initialMessage.inbound.widgetMessage.widgetSession.email',
    'initialMessage.inbound.widgetMessage.widgetSession.userId',
    'initialMessage.inbound.microsoftTeamsMessage.id',
    'initialMessage.inbound.microsoftTeamsMessage.subject',
    'initialMessage.inbound.microsoftTeamsMessage.content',
    'initialMessage.inbound.microsoftTeamsMessage.contentType',
    'latestMessage.id',
    'latestMessage.ts',
    'latestMessage.threadTs',
    'latestMessage.timestamp',
    'ticketType.id',
    'ticketType.name',
    'ticketTypeFields',
    'customer.slackChannel.name',
    'customer.slackTeams.icon',
    'externalTasks.status',
    'externalTasks.name',
    'externalTasks.url',
    'project.id',
    'project.name',
    'project.visibility',
    'account.id',
    'account.name',
    'submitterUser.id',
    'submitterUser.name',
    'submitterUser.photo',
    'submitterUser.email',
    'submitterUser.isExternal',
    'metadata',
    'subStatusId',
    'subStatus.key',
    'subStatus.name',
    'escalations.title',
    'escalations.status',
    'escalations.createdAt',
    'escalations.updatedAt',
    'escalations.acknowledgedAt',
    'escalations.resolvedAt',
  ];

  fetchConversations = async ({
    isExport = false,
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
    ids,
  }: FetchDataRequest & { isExport?: boolean; ids?: string[] } = {}): Promise<
    FetchDataResponse<Partial<Conversation>>
  > => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
      select: this.select,
      includeIntake: this.selectedTab?.key === INBOX_TYPES.INTAKE,
    };

    if (isExport) {
      options.select.push(
        'latestInternalMessage.id',
        'latestInternalMessage.timestamp',
        'latestExternalMessage.id',
        'latestExternalMessage.timestamp',
        'ticketTypeFields',
        'triageThreads.id',
        'triageThreads.ts',
        'triageThreads.channelId',
        'triageThreads.channelName',
        'triageThreads.isChannel',
        'triageThreads.isCustomerFacing',
        'triageThreads.escalationId',
        'triageThreads.createdAt',
        'triageThreads.noteObject',
        'triageThreads.timezone',
      );
    }

    const where = this.getWhereClauses(undefined, ids);

    if (where.length) {
      options.where = where;
    }

    const result = await this.conversationsService.list(options, true);

    result.data = result.data.map((c) => {
      const transformedInitialMessage = c.initialMessage ? transformMessage(c.initialMessage) : undefined;

      return {
        ...c,
        initialMessage: transformedInitialMessage,
        submitterUser: c.submitterUser
          ? {
              ...c.submitterUser,
              link: formatLink(c.submitterUser.id, c.submitterUser.isExternal),
            }
          : transformedInitialMessage
            ? {
                photo: transformedInitialMessage.image,
                name: transformedInitialMessage.name,
                link: transformedInitialMessage.link,
                userType: transformedInitialMessage.userType,
              }
            : {},
      };
    });

    return result;
  };

  fetchEscalations = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
    ids,
  }: FetchDataRequest & { isExport?: boolean; ids?: string[] } = {}): Promise<
    FetchDataResponse<Partial<Escalation>>
  > => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
      select: [
        'id',
        'title',
        'body',
        'publicNote',
        'priority',
        'status',
        'createdAt',
        'updatedAt',
        'acknowledgedAt',
        'resolvedAt',
        'escalationTypeId',
        'assignedToUserId',
        'assignedToGroupId',
        'conversationId',
        'escalationType.name',
        'assignedToUser.name',
        'assignedToUser.photo',
        'assignedToGroup.name',
        'triageThread.initialMessage.ts',
        'triageThread.initialMessage.channelId',
        'triageThread.initialMessage.slackChannel.name',
        'conversation.ticketType.name',
        'conversation.ticketType.id',
        'conversation.friendlyId',
        'conversation.title',
      ],
    };

    const where: any[] = [];

    if (this.authService.selectedProject) {
      where.push({
        field: 'escalationType.projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    if (this.isFilteringEscalationsByMe) {
      where.push({
        field: 'assignedToUserId',
        operator: '==',
        value: this.authService.userId,
      });
    }

    const result = await this.escalationsService.list({ where, search: this.searchText || undefined });

    const assignedToMe = result.data.filter(
      (e) => e.assignedToUserId === this.authService.userId && e.status === ESCALATION_STATUSES.PENDING,
    ).length;

    document.dispatchEvent(new CustomEvent('escalation-updated', { detail: { assignedToMe } }));

    return result;
  };

  conversationsDataSource = createDataSource(this.fetchConversations);
  escalationsDataSource = createDataSource(this.fetchEscalations);

  onSaveFilter = async (savedFilter: any) => {
    document.dispatchEvent(new CustomEvent('saved-filter', { detail: savedFilter }));
  };

  loadConversationsDebounced = _.debounce(() => this.loadConversations(), 1000);

  public isURL = isUrl;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private navController: NavController,
    public authService: AuthService,
    private tagsService: TagsService,
    private conversationsService: ConversationsService,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private usersSrvc: UsersService,
    private serverEventsService: ServerEventsService,
    private searchablePopoverService: SearchablePopoverService,
    private analyticsService: AnalyticsService,
    private apiService: ApiService,
    private msgService: MessageService,
    private emojiService: EmojisService,
    private collaboratorTypesService: CollaboratorTypesService,
    private customFieldsService: CustomFieldsService,
    private escalationsService: EscalationsService,
  ) {}

  @HostListener('window:popstate', ['$event'])
  onPopState() {
    this.ionViewWillEnter();
  }

  async ionViewWillEnter() {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const filterParams = params.get('filters');
    const viewParam = params.get('view');
    const cursorParam = params.get('cursor');
    const pathParts = url.pathname.split('/');
    const trailingObjectId = pathParts.pop();
    const routeType = pathParts.pop();

    if (routeType === 'view' && trailingObjectId) {
      this.selectedSidebarConversationId = trailingObjectId;
    }
    if (routeType === 'filter' && trailingObjectId) {
      this.savedFilterId = trailingObjectId;
    }

    if (viewParam) {
      this.statusIndex = Number(viewParam);
    }

    if (cursorParam && this.conversationsDataSource.lastRequest) {
      this.cursor = JSON.parse(cursorParam);
    }

    if (filterParams) {
      this.filters = new Map(JSON.parse(filterParams));
    } else if (this.savedFilterId) {
      const savedFilter: any = await this.apiService.getPromise(`/saved-filters/${this.savedFilterId}`);

      const filtersAsArray: { key: string; operator: SimpleConditionsOperator; values: string[] }[] = [];

      if (Array.isArray(savedFilter?.filters)) {
        for (const filterClause of savedFilter.filters) {
          if (filterClause && filterClause.field) {
            filtersAsArray.push({
              key: filterClause.field,
              operator: filterClause.operator,
              values: Array.isArray(filterClause.value) ? filterClause.value : [filterClause.value],
            });
          }
        }
      }

      const hydratedMapWithSpecificKeys = await reconstructAndHydrateUiFilters(
        filtersAsArray,
        savedFilter?.lookups ?? {},
        this.filterOptions,
        this.apiService,
        this.customFieldsService,
      );

      const finalFiltersMap = new Map<FilterOption, FilterValue[]>();

      for (const [option, values] of hydratedMapWithSpecificKeys.entries()) {
        finalFiltersMap.set(option, values);
      }

      this.filters = finalFiltersMap;
    } else {
      this.filters = new Map();

      for (const filter of this.selectedTab?.filters ?? []) {
        const filterOption = this.filterOptions.find((f) => f.id === filter.field);

        if (filterOption) {
          this.filters.set(
            filterOption as FilterOption,
            filter.values.map((v) => ({ id: v.id, label: v.title })),
          );
        }
      }
    }

    if (!this.cursor) {
      this.loadConversations();
    }
  }

  async ngOnInit() {
    this.emailInboxId = this.route.snapshot.paramMap.get('ruleId');
    this.inAppInboxId = this.route.snapshot.paramMap.get('widgetId');
    this.microsoftTeamsInboxId = this.route.snapshot.paramMap.get('teamsId');
    this.savedFilterId = this.route.snapshot.paramMap.get('savedFilterId');

    const inboxLayoutPreference: any = localStorage.getItem('inboxLayout');
    this.layoutType = inboxLayoutPreference ?? 'grid';

    // Initialize table columns configuration
    this.loadTableColumnsFromStorage();

    const statusParam = this.route.snapshot.queryParamMap.get('view');

    this.statusIndex = statusParam ? Number(statusParam) : 0;

    const profile = this.authService.profile;
    const savedSort = this.sortOptions[profile.slackInboxState?.sort];
    if (savedSort) {
      this.sortDesc = savedSort.sortDesc;
      this.sortField = savedSort.sortField;
      this.sortLabel = savedSort.label;
    }

    this.routerSub = this.router.events
      .pipe(
        distinctUntilChanged((previous, current) => {
          if (previous instanceof NavigationEnd && current instanceof NavigationEnd) {
            return previous.url === current.url;
          }

          return true;
        }),
      )
      .subscribe(() => {
        const cleanedUrl = this.router.url.split('?')[0];
        const inboxType = cleanedUrl.split('inbox/')[1];

        this.selectedTab = null;
        // Hacky check to see if you were sent a URL from the sidebar view but don't have it enabled
        if (cleanedUrl.includes('/view/') && localStorage.getItem('inboxLayout') !== INBOX_LAYOUT_TYPES.SIDEBAR) {
          this.navController.navigateRoot(cleanedUrl.replace('/view/', '/conversations/'));
          return;
        }

        this.tabs.forEach((tab) => {
          if (inboxType.startsWith(tab.key)) {
            this.selectedTab = tab;
          }
        });
      });
    // Sizing for mobile
    if (window.innerWidth < 540) {
      this.tableRowHeight = 'auto';
      this.tableMessageWidth = 260;
      this.tableShowId = false;
      this.layoutType = 'grid';
      this.dragEnabled = false;
    }
    // Subscribe to live updates
    this.subscription = this.serverEventsService.subscribe(
      [
        'conversation:created',
        'conversation:changed_status',
        'conversation:reassigned',
        'conversation:escalated',
        'conversation:tags_added',
        'conversation:tags_removed',
      ],
      async (data) => {
        try {
          if (data.type === 'conversation:changed_status') {
            this.loadConversationsDebounced(false);

            const conversation = await this.fetchConversation(data.data.conversationId);

            if (conversation && this.layoutType === 'grid') {
              for (const convoList of this.convoLists) {
                if (
                  convoList.whereClauses.some(
                    (w) => w.field === 'subStatus.key' && w.operator === '==' && w.value === conversation.subStatus.key,
                  )
                ) {
                  convoList.addConversation(conversation);
                } else {
                  convoList.removeConversation(conversation);
                }
              }
            }
          } else {
            this.loadConversationsDebounced();
          }
        } catch (error) {
          if (error.status >= 500) {
            this.apiService.postPromise('/log-entries', {
              level: 'error',
              data: {
                message: error.message,
                stack: error.stack,
                code: error.code,
              },
            });
          }
        }
      },
    );

    this.bulkUpdateSubscription = this.serverEventsService.subscribe(
      ['conversation:bulk_update_completed'],
      (data: any) => {
        if (data.data.runId === this.bulkUpdateRunId) {
          this.isProcessingBulkUpdate = false;

          if (data.data.error) {
            this.msgService.showError(
              `An error occurred while processing bulk conversation ${data.data.isDeleted ? 'deletes' : 'updates'}. Some conversations may have been ${data.data.isDeleted ? 'deleted' : 'updated'}. Received error: ${data.data.error}`,
            );
          } else {
            this.msgService.show(`Successfully ${data.data.isDeleted ? 'deleted' : 'updated'} conversations in bulk`);
          }

          this.loadConversationsDebounced();
        }
      },
    );

    this.conversationsSubscription = this.conversationsDataSource.subscribe((data) => {
      this.isOnFirstPage = !data?.[0]?.cursors?.hasPrevious;
      this.cursor = this.isOnFirstPage ? undefined : data?.[0].cursors;
      this.selectAllPagesForBulkUpdate = false;
      this.clearSelectedConversations();

      if (this.cursor) {
        this.updateQueryParams();
      }
    });

    // Subscribe to escalations data source
    this.escalationsSubscription = this.escalationsDataSource.subscribe((data) => {
      this.isOnFirstPage = !data?.[0]?.cursors?.hasPrevious;
      this.cursor = this.isOnFirstPage ? undefined : data?.[0].cursors;
      this.selectAllPagesForBulkUpdate = false;
      this.clearSelectedConversations();

      if (this.cursor) {
        this.updateQueryParams();
      }
    });

    document.addEventListener(
      'inbox-layout-change',
      () => {
        const inboxLayoutPreference: any = localStorage.getItem('inboxLayout');

        if (inboxLayoutPreference === this.layoutType) {
          return;
        }
        // make a weird attempt to inject the sidebar if viewing a conversation
        if (inboxLayoutPreference === INBOX_LAYOUT_TYPES.SIDEBAR && this.layoutType !== INBOX_LAYOUT_TYPES.SIDEBAR) {
          const cleanedUrl = this.router.url.split('?')[0];
          const split = cleanedUrl.split('/conversations/');
          if (split?.length > 0 && split[1]) {
            const convoId = split[1].split('/');
            this.navController.navigateRoot(
              `/${routes.DASHBOARD}/${routes.INBOX}/${this.getInboxType()}/view/${convoId[0]}`,
            );
          }
        }

        this.layoutType = inboxLayoutPreference;
        this.loadConversations();
      },
      false,
    );

    Promise.all([this.emojiService.loadEmojis(), this.loadCollaboratorTypes()]);
    await this.loadSubStatuses();
  }

  async loadSubStatuses() {
    this.subStatuses = await this.conversationsService.getCachedSubstatuses(this.authService.selectedProject?.id);

    this.kanbanColumns = this.subStatuses
      .map((s) => {
        this.subStatusKeyCount[s.key] = -1;

        return {
          key: s.key,
          label: s.name,
          icon: STATUS_COLUMN_MAP[s.type]?.icon,
          cssClass: STATUS_COLUMN_MAP[s.type]?.cssClass,
          countColor: STATUS_COLUMN_MAP[s.type]?.countColor,
          componentAttribute: s.key,
        };
      })
      .filter((c) => c !== null);
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.subscription?.unsubscribe();
    this.bulkUpdateSubscription?.unsubscribe();
    this.conversationsSubscription?.unsubscribe();
    this.escalationsSubscription?.unsubscribe();
    document.removeEventListener('inbox-layout-change', () => {}, false);
  }

  async loadConversations() {
    this.isLoading = true;

    if (this.selectedTab?.key !== INBOX_TYPES.ESCALATIONS) {
      // Load conversations
      await this.conversationsDataSource.firstPage();

      // Make attempt to update the count on sidebar
      if (this.layoutType === 'sidebar') {
        this.sidebarConvoList.loadConversations();
        this.sidebarConvoCount = this.conversationsDataSource.lastResponse.totalCount;
      }

      if (this.layoutType === 'grid') {
        this.convoLists.forEach((convoList) => {
          convoList.loadConversations();
        });
      }
    }

    if (this.selectedTab?.key === INBOX_TYPES.ESCALATIONS || this.selectedTab?.key === INBOX_TYPES.ME) {
      await this.escalationsDataSource.firstPage();
    }

    this.isLoading = false;
  }

  getWhereClauses(kanbanStatus?: string, ids?: string[]) {
    const where: any[] = [];

    // Handle conversation-specific filters
    if (this.emailInboxId) {
      where.push({
        field: 'inboundEmailRuleId',
        operator: '==',
        value: this.emailInboxId,
      });
    } else if (this.inAppInboxId) {
      where.push({
        field: 'widgetId',
        operator: '==',
        value: this.inAppInboxId,
      });
    } else if (this.microsoftTeamsInboxId) {
      where.push({
        field: 'externalChatConnectionId',
        operator: '==',
        value: this.microsoftTeamsInboxId,
      });
    } else if (this.selectedTab?.key === INBOX_TYPES.COLLABORATING_ON) {
      where.push({
        field: 'collaboratorsFlat.userId',
        operator: '==',
        value: this.authService.userId,
      });
    } else if (this.selectedTab?.key === INBOX_TYPES.FOLLOWING) {
      where.push({
        field: 'followersFlat.userId',
        operator: '==',
        value: this.authService.userId,
      });
    }

    if (kanbanStatus) {
      where.push({
        field: 'subStatus.key',
        operator: '==',
        value: kanbanStatus,
      });
    } else if (
      this.selectedTab?.key !== INBOX_TYPES.CLOSED &&
      this.selectedTab?.key !== INBOX_TYPES.INTAKE &&
      !this.savedFilterId &&
      this.layoutType !== 'grid' && // Don't filter by status when exporting in the kanban view
      [0 /** Open */, 1 /** On Hold */, 2 /** Closed */].includes(this.statusIndex) // Add filters when viewing a specific status tab (except for All - 3)
    ) {
      const VALUES = [
        [CONVERSATION_STATUS.OPEN, CONVERSATION_STATUS.IN_PROGRESS],
        CONVERSATION_STATUS.ON_HOLD,
        CONVERSATION_STATUS.CLOSED,
      ];

      const OPERATORS = ['in', '==', '=='];

      where.push({
        field: 'status',
        operator: OPERATORS[this.statusIndex],
        value: VALUES[this.statusIndex],
      });
    }

    if (this.authService.selectedProject) {
      where.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    const filterWheres = getWhereClausesFromFilters(this.filters, this.authService.userId);

    where.push(...filterWheres);

    if (ids) {
      where.push({
        field: 'id',
        operator: 'in',
        value: ids,
      });
    }

    return where;
  }

  kanbanSelectionDidChange(event: { _conversation: Conversation; _index: number }) {
    this.selectedConversations = this.convoLists.reduce((acc, convoList) => {
      return [...acc, ...convoList.selectedConversations];
    }, []);
  }

  sidebarSelectionDidChange(event: { conversation: Conversation; index: number }) {
    this.selectedSidebarConversationId = event.conversation?.id ?? undefined;
    this.selectedSidebarConversationIndex = event.index ?? -1;
  }

  getInboxType() {
    return this.emailInboxId
      ? `email/${this.emailInboxId}`
      : this.inAppInboxId
        ? `chat/${this.inAppInboxId}`
        : this.microsoftTeamsInboxId
          ? `microsoft-teams/${this.microsoftTeamsInboxId}`
          : this.savedFilterId
            ? `filter/${this.savedFilterId}`
            : this.selectedTab?.key ?? INBOX_TYPES.ME;
  }

  getBaseRouterLink() {
    return `/${routes.DASHBOARD}/${routes.INBOX}/${this.getInboxType()}/conversations`;
  }

  cacheConversation(conversation: Conversation) {
    this.conversationsService.cachedConversation = conversation;
  }

  viewCustomer(customerId: string) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.CUSTOMERS}/${customerId}/overview`);
  }

  viewUser(userId: string) {
    this.navController.navigateRoot(`${routes.PEOPLE}/${userId}`);
  }

  getTagsForRow(row: any) {
    return row?.tags?.map((tag) => tag.name).join(', ');
  }

  displayDate(date: Date) {
    return moment(date).from(moment());
  }

  formatContent(conversation: Conversation, maxLength?: number) {
    return formatConversationContent(conversation, maxLength);
  }

  changeStatus(row: Conversation) {
    this.searchablePopoverService.statuses({
      event,
      selectedStatusId: row.status,
      callback: async (status: string) => {
        this.analyticsService.trackEvent('Inbox', 'Change Status');

        const originalStatus = row.status;
        try {
          row.status = status;
          await this.conversationsService.update(row.id, { status });
        } catch (error) {
          // Revert the local status change
          row.status = originalStatus;
          this.msgService.showError(error);
        }
      },
    });
  }

  changeAssignee(row: Conversation) {
    this.searchablePopoverService.users({
      event,
      selectedUserId: row.assignedToUserId,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Inbox', 'Change Assignee');

        await this.conversationsService.update(row.id, { assignedToUserId: id });
        this.loadConversations();
      },
    });
  }

  changePriority(row: Conversation) {
    this.searchablePopoverService.priorities({
      event,
      selectedPriority: String(row.priority),
      callback: async (priority: string) => {
        this.analyticsService.trackEvent('Inbox', 'Change Priority');

        row.priority = Number(priority);
        await this.conversationsService.update(row.id, { priority: Number(priority) });
      },
    });
  }

  async changeEscalationStatus(escalation: Partial<Escalation>, event: Event) {
    this.searchablePopoverService.escalationStatuses({
      event,
      selectedStatusId: escalation.status,
      callback: async (id: string) => {
        if (id && id !== escalation.status) {
          await this.updateEscalation(escalation, { status: id });
        }
      },
    });
  }

  async updateEscalation(escalation: Partial<Escalation>, changes: Partial<Escalation>) {
    try {
      await this.escalationsService.updatePartial(escalation.id, changes);
      this.msgService.show('✔︎ Escalation updated', { duration: 2000, position: 'bottom' });
      // Refresh the escalations data
      this.escalationsDataSource.reloadPage();
    } catch (error) {
      this.msgService.showError(error);
    }
  }

  async changeEscalationAssignee(escalation: Partial<Escalation>, event: Event) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: escalation.assignedToGroupId ?? escalation.assignedToUserId,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      includeNonAgents: true,
      showNoneOption: true,
      includeGroups: true,
      callback: async (id: string, name: string, object: any) => {
        if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.USER || !id) {
          await this.updateEscalation(escalation, {
            assignedToUserId: id,
            assignedToGroupId: null,
          });
        } else if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.TEAM) {
          const selectedGroupId = id;
          await this.promptForUserInGroup(escalation, selectedGroupId, event);
        }
      },
    });
  }

  async promptForUserInGroup(escalation: Partial<Escalation>, groupId: string, event: Event) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: escalation.assignedToGroupId === groupId ? escalation.assignedToUserId : undefined,
      showAutoAssignFromGroupOption: true,
      showNoneOption: true,
      includeNonAgents: true,
      includeGroups: false,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      userGroupId: groupId,
      callback: async (selectedUserId: string, name: string, object: any) => {
        await this.updateEscalation(escalation, {
          assignedToUserId: selectedUserId,
          assignedToGroupId: groupId,
        });
      },
    });
  }

  onSelect({ selected }) {
    this.selectedConversations.splice(0, this.selectedConversations.length);
    this.selectedConversations.push(...selected);

    if (selected?.length < this.limit) {
      this.selectAllPagesForBulkUpdate = false;
    }
  }

  selectTab(tab) {
    this.selectedTab = tab;
    this.filters = new Map();

    for (const filter of tab.filters ?? []) {
      const filterOption = this.filterOptions.find((f) => f.id === filter.field);

      if (filterOption) {
        this.filters.set(filterOption as FilterOption, filter.values);
      }
    }

    this.loadConversations();
  }

  getColumnCount(status: Statuses): number {
    return this.subStatusKeyCount[status] ?? 0;
  }

  async bulkEditTags() {
    const modal = await this.modalCtrl.create({
      component: EditTagLinksComponent,
      componentProps: {
        objectIds: this.selectedConversations.map((c) => c.id),
        addCallback: async (tagId, conversationIds) => {
          await this.tagsService.createConversationLinks(tagId, conversationIds);
          this.loadConversations();
          this.clearSelectedConversations();
        },
        removeCallback: async (tagId, conversationIds) => {
          await this.tagsService.deleteConversationLinks(tagId, conversationIds);
          this.loadConversations();
          this.clearSelectedConversations();
        },
      },
    });

    modal.present();
  }

  async bulkChangeStatus() {
    this.searchablePopoverService.subStatusKeys({
      event,
      projectId: this.authService.selectedProject?.id,
      callback: async (_value: string, _name: string, subStatus: SubStatus) => {
        this.analyticsService.trackEvent('Inbox', 'Bulk Change Status');

        this.bulkUpdateConversations({
          subStatusId: subStatus.id,
        });
      },
    });
  }

  async bulkChangeAssignee() {
    this.searchablePopoverService.users({
      event,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Inbox', 'Bulk Change Assignee');

        this.bulkUpdateConversations({
          assignedToUserId: id,
        });
      },
    });
  }

  async bulkChangeTicketType() {
    this.searchablePopoverService.ticketTypes({
      event,
      projectId: this.authService.selectedProject?.id,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Inbox', 'Bulk Change Ticket Type');

        this.bulkUpdateConversations({
          ticketTypeId: id,
        });
      },
    });
  }

  async bulkOptions() {
    const options: ActionDropdownType[] = [
      { label: 'Exclude from analytics', icon: 'cloud-offline-outline', destructive: false },
      { label: 'Delete', icon: 'trash-outline', destructive: true },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          if (idx === 0) {
            this.bulkUpdateConversations({
              excludeAnalytics: true,
            });
          } else if (idx === 1) {
            this.bulkUpdateConversations({
              isDeleted: true,
            });
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async bulkUpdateConversations(updates: any) {
    this.bulkUpdateCount = this.selectAllPagesForBulkUpdate
      ? this.conversationsDataSource.lastResponse.totalCount
      : this.selectedConversations.length;

    const alert = await this.alertCtrl.create({
      header: `Confirm bulk ${updates.isDeleted ? 'delete' : 'update'}${this.selectAllPagesForBulkUpdate ? ' ALL conversations that match this filter?' : ''}`,
      subHeader: `This will ${updates.isDeleted ? 'delete' : 'update'} ${this.bulkUpdateCount} conversation${
        this.bulkUpdateCount === 1 ? '' : 's'
      } and cannot be undone.`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Confirm',
          handler: async () => {
            alert.dismiss();

            this.isProcessingBulkUpdate = true;

            try {
              let bulkUpdateWhere: any[] = [];

              if (this.selectAllPagesForBulkUpdate) {
                bulkUpdateWhere = this.getWhereClauses();
              } else {
                const whereIds = this.selectedConversations.map((c) => c.id);

                if (!whereIds.length) {
                  return;
                }

                bulkUpdateWhere = [
                  {
                    field: 'id',
                    operator: 'in',
                    value: whereIds,
                  },
                ];
              }

              const { runId } = (await this.conversationsService.bulkUpdate(bulkUpdateWhere, updates)) as any;

              this.bulkUpdateRunId = runId;

              this.clearSelectedConversations();

              this.analyticsService.trackEvent(
                'Inbox',
                `Bulk ${updates.isDeleted ? 'Delete' : 'Update'}${this.selectAllPagesForBulkUpdate ? ' All' : ''} Conversations`,
              );

              this.selectAllPagesForBulkUpdate = false;
            } catch (err: any) {
              this.msgService.showError(err);
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async showSortOptions() {
    const sortOptionsArray = _.values(this.sortOptions);
    const options: ActionDropdownType[] = sortOptionsArray.map((opt) => {
      return {
        label: opt.label,
        icon: opt.label === this.sortLabel ? 'checkmark-outline' : 'lol',
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          const sort = sortOptionsArray[idx];
          this.sortDesc = sort.sortDesc;
          this.sortField = sort.sortField;
          this.sortLabel = sort.label;

          this.loadConversations();

          this.usersSrvc.updateUser(this.authService.userId, {
            slackInboxState: {
              ...this.authService.profile.slackInboxState,
              sort: sort.key,
            },
          });

          this.analyticsService.trackEvent('Inbox', `Change Sort to ${this.sortLabel}`);
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async showCsvDownloadOptions() {
    const alert = await this.alertCtrl.create({
      header: 'Download CSV',
      message: 'Choose what to download:',
      buttons: [
        {
          text: 'Download current view',
          handler: () => {
            // Export the conversations currently visible on screen
            const currentViewConversations = this.conversationsDataSource.lastResponse?.data || [];
            this.exportAsCsv(currentViewConversations);
          },
        },
        {
          text: 'Download all',
          handler: () => {
            // Export all conversations matching current filters (no conversations parameter)
            this.exportAsCsv(undefined, true);
          },
        },
        {
          text: 'Cancel',
          role: 'cancel',
        },
      ],
    });
    await alert.present();
  }

  async viewSummary(conversation: Conversation) {
    const alert = await this.alertCtrl.create({
      header: `Summary`,
      subHeader: conversation.summary,
    });

    await alert.present();

    this.analyticsService.trackEvent('Inbox', 'View Summary');
  }

  async viewNotes(conversation: Conversation) {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: conversation.notes,
        title: 'Note',
        callback: async (text: string) => {
          conversation.notes = text;
          await this.conversationsService.update(conversation.id, { notes: text });
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async viewTasks(conversation: Conversation) {
    const options: ActionDropdownType[] = conversation.externalTasks.map((t) => {
      return { label: `${t.isCompleted ? '[Done]' : '[Todo]'} ${t.name}`, icon: 'open-outline' };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          window.open(conversation.externalTasks[idx].url, '_blank');
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async viewSentiment(conversation: Conversation) {
    const nlpResult = conversation.initialMessage.nlpResult;

    const alert = await this.alertCtrl.create({
      header: `Sentiment`,
      subHeader: JSON.stringify(nlpResult),
    });

    await alert.present();

    this.analyticsService.trackEvent('Inbox', 'View Notes');
  }

  async exportAsCsv(conversations?: Partial<Conversation>[], allConversations = false) {
    this.analyticsService.trackEvent('Inbox', 'Export CSV');

    this.isDownloadingCsv = true;

    const lastRequest = this.conversationsDataSource.lastRequest;
    const date = new Date();

    try {
      // List was already provided, no need to change anything
      if (conversations && conversations.length > 0) {
        // We have specific conversations to export (current view)
        // Re-fetch them with full export data to ensure we have all fields
        const response = await this.fetchConversations({
          isExport: true,
          limit: null,
          order: lastRequest?.order || ['createdAt'],
          descending: lastRequest?.descending || true,
          ids: conversations.map((c) => c.id),
        });

        conversations = response.data;
      } else {
        // Export all conversations matching current filters
        const response = await this.fetchConversations({
          isExport: true,
          limit: allConversations ? null : this.limit,
          order: lastRequest?.order || ['createdAt'],
          descending: lastRequest?.descending || true,
        });

        conversations = response.data;
      }

      const timezone = this.authService.tenant.workingHours?.timezone || moment.tz.guess();
      const formatDate = (date: Date) => (date ? moment(date).tz(timezone).format('YYYY-MM-DD HH:mm:ss') : '');

      const results = conversations.map((conversation: Conversation) => {
        const hasOutstandingExternalTasks: boolean | string =
          conversation.externalTasks && conversation.externalTasks.length
            ? conversation.externalTasks.reduce((acc, task) => acc || !task.isCompleted, false)
            : '';

        return {
          ...conversation,
          tagList: conversation.tags?.map((tag) => tag.name),
          title: conversation.initialMessage?.resolvedContent ? this.formatContent(conversation) : '',
          customerName: conversation.customer?.name, // maintain for backwards compatibility
          channelName: conversation.customer?.name,
          accountName: conversation.account?.name,
          accountId: conversation.account?.id,
          customerTagsList: conversation.customer?.tags?.map((tag) => tag.name),
          assignedToUserName: conversation.assignedToUser?.name,
          submitterUserId: conversation.submitterUserId,
          submitterUserName: conversation.submitterUser?.name,
          submitterUserEmail: conversation.submitterUser?.email,
          slackLink: getSlackLink(
            conversation.teamId,
            conversation.latestMessage?.ts,
            conversation.latestMessage?.threadTs,
            conversation.channelId,
            this.authService.tenant.slackTeamUrl,
          ),
          createdAt: formatDate(conversation.createdAt),
          updatedAt: formatDate(conversation.updatedAt),
          closedAt: formatDate(conversation.closedAt),
          statusUpdatedAt: formatDate(conversation.statusUpdatedAt),
          respondedAt: formatDate(conversation.respondedAt),
          latestMessageAt: formatDate(conversation.latestMessage?.timestamp),
          latestExternalMessageAt: formatDate(conversation.latestExternalMessage?.timestamp),
          latestInternalMessageAt: formatDate(conversation.latestInternalMessage?.timestamp),
          ticketTypeId: conversation.ticketType?.id,
          ticketTypeName: conversation.ticketType?.name,
          ticketTypeFields: conversation.ticketTypeFields ?? [],
          notes: conversation.notes,
          hasOutstandingExternalTasks,
          externalTasks:
            conversation.externalTasks && conversation.externalTasks.length ? conversation.externalTasks : '',
          triageChannels: conversation.triageThreads?.map((m) => m.channelName ?? PRIVATE_CHANNEL_NAME),
          metadata: JSON.stringify(conversation.metadata),
          subStatus: conversation.subStatus?.name,
        };
      });

      const headers = [
        'id',
        'title',
        'status',
        'subStatus',
        'friendlyId',
        'priority',
        'createdAt',
        'updatedAt',
        'statusUpdatedAt',
        'respondedAt',
        'responseTime',
        'responseTimeWorking',
        'closedAt',
        'resolutionTime',
        'resolutionTimeWorking',
        'customerId',
        'customerName',
        'accountId',
        'accountName',
        'submitterUserId',
        'submitterUserName',
        'submitterUserEmail',
        'assignedToUserId',
        'assignedToUserName',
        'slackLink',
        'tagList',
        'customerTagsList',
        'summary',
        'latestMessageAt',
        'latestExternalMessageAt',
        'latestInternalMessageAt',
        'ticketTypeId',
        'ticketTypeName',
        'ticketTypeFields',
        'notes',
        'sentiment',
        'language',
        'hasOutstandingExternalTasks',
        'externalTasks',
        'triageChannels',
        'metadata',
      ];

      downloadCSV(results, headers, `inbox-export-${date.getTime()}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      this.msgService.showError('Failed to export CSV');
    } finally {
      this.isDownloadingCsv = false;
    }
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    // Semi-hacky way of making sure they don't double filter
    if (this.statusIndex !== 3) {
      this.filters.forEach((_filter: FilterValue[], key: FilterOption) => {
        if (key.id === 'status') {
          this.statusIndex = 3;
        }
      });
    }

    this.updateQueryParams();
    this.loadConversations();
  }

  updateQueryParams() {
    this.navController.navigateRoot([], {
      queryParams: {
        filters: JSON.stringify([...this.filters]),
        cursor: JSON.stringify(this.cursor),
      },
      queryParamsHandling: 'merge',
    });
  }

  selectStatus(index: number) {
    this.statusIndex = index;

    this.navController.navigateRoot([], {
      queryParams: {
        view: this.statusIndex,
      },
      queryParamsHandling: 'merge',
    });

    this.loadConversations();
  }

  onSearch() {
    this.loadConversations();
  }

  getSentimentVote(conversation: Conversation) {
    return conversation.sentiment;
  }

  displayDuration(durationSecs: number | null) {
    if (!durationSecs) return '';
    return getDisplayTextForDuration(durationSecs);
  }

  // Escalation-specific helper methods
  getEscalationStatusIcon(status: string): string {
    switch (status) {
      case ESCALATION_STATUSES.PENDING:
        return 'custom-open';
      case ESCALATION_STATUSES.ACKNOWLEDGED:
        return 'custom-in-progress';
      case ESCALATION_STATUSES.RESOLVED:
        return 'custom-closed';
      default:
        return 'help-circle';
    }
  }

  getEscalationStatusColor(status: string): string {
    switch (status) {
      case ESCALATION_STATUSES.PENDING:
        return 'danger';
      case ESCALATION_STATUSES.ACKNOWLEDGED:
        return 'warning';
      case ESCALATION_STATUSES.RESOLVED:
        return 'success';
      default:
        return 'medium';
    }
  }

  displayEscalationDate(date: Date) {
    if (!date) return '';
    return moment(date).fromNow();
  }

  displayEscalationPriority(conversation: Conversation) {
    if (!conversation.priority) {
      return '';
    }

    const emoji = conversation.priorityEmoji;
    const label = conversation.priorityLabel;

    if (!emoji || !label) {
      return '';
    }

    if (this.isURL(emoji)) {
      return `<img src="${emoji}" alt="${label}" style="width: 1em; height: 1em; vertical-align: -0.125em;" /> ${label}`;
    } else {
      return `${emoji} ${label}`;
    }
  }

  getEscalationAssignee(escalation: Partial<Escalation>) {
    if (escalation.assignedToUser) {
      return escalation.assignedToUser.name;
    } else if (escalation.assignedToGroup) {
      return escalation.assignedToGroup.name;
    } else {
      return 'Unassigned';
    }
  }

  openEscalation(escalation: Partial<Escalation>) {
    // Navigate to the conversation with the escalation
    this.navController.navigateForward(
      `/dashboard/inbox/escalations/conversations/${escalation.conversationId}?escalationId=${escalation.id}`,
    );
  }

  clearSelectedConversations() {
    this.selectedConversations = [];
    this.selectAllPagesForBulkUpdate = false;

    this.convoLists.forEach((convoList) => {
      convoList.clearSelectedConversations();
    });
  }

  setLayoutType(type: 'grid' | 'table' | 'sidebar') {
    this.layoutType = type;
    localStorage.setItem('inboxLayout', this.layoutType);

    this.analyticsService.trackEvent('Inbox', `Set Type to ${this.layoutType}`);

    this.loadConversations();
  }

  async showKanbanOptions(type: string) {
    const options: ActionDropdownType[] = [
      {
        label: 'Select all',
        icon: 'checkbox-outline',
      },
      {
        label: 'Download as CSV',
        icon: 'cloud-download-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          const conversationList = this.convoLists.find((v) => v.viewTag === type);
          const conversations = conversationList.conversations;

          if (idx === 0) {
            conversationList.selectConversations(conversations);
          } else if (idx === 1) {
            this.exportAsCsv(conversations);
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  kanbanDidUpdateCount(event: { viewTag: string; count: number }) {
    if (event.viewTag) {
      this.subStatusKeyCount[event.viewTag] = event.count;
    } else if (this.layoutType === 'sidebar') {
      this.sidebarConvoCount = event.count;

      if (this.sidebarConvoCount && this.selectedSidebarConversationId && this.selectedSidebarConversationIndex < 0) {
        // We likely got here from a deeplink and need to find out which conversation to highlight in the left nav
        this.sidebarConvoList.conversations.forEach((conversation: Conversation, index: number) => {
          if (conversation.id === this.selectedSidebarConversationId) {
            this.selectedSidebarConversationIndex = index;
            this.sidebarConvoList.manuallyRouteToConversationAtIndex(index);
            return;
          }
        });
      } else if (this.sidebarConvoCount && !this.selectedSidebarConversationId) {
        // In this case we're likely loading a list for the first time
        this.selectedSidebarConversationIndex = 0;
        this.selectedSidebarConversationId = this.sidebarConvoList.conversations[0].id;
        this.sidebarConvoList.manuallyRouteToConversationAtIndex(0);
      } else {
        this.selectedSidebarConversationIndex = -1;
      }
    }
  }

  kanbanDidStartDragging(conversation: Conversation) {
    this.draggedConversation = conversation;
  }

  kanbanDidStopDragging(event: DragEvent) {
    this.dragHighlightedCol = undefined;
  }
  // FUTURE TODO eng-4048 counts need to be updated based on column names (update from hardcoded vars)
  async onDrop(event: DragEvent, newSubStatus: string): Promise<void> {
    event.preventDefault();

    if (!this.draggedConversation) {
      // Nothing to see here;
      // We previously checked if statuses were different, but this caused Conversation #3538
      return;
    }

    const projectId = this.draggedConversation.projectId ?? this.draggedConversation.project?.id;
    const subStatus = this.subStatuses.find((s) => s.key === newSubStatus);
    const subStatusId = subStatus?.projects.find((p) => p.projectId === projectId)?.id;

    if (!subStatusId) {
      this.msgService.show(`This conversation's project doesn't support this status`);
      return;
    }

    // This if statement is also due to Conversation #3538
    if (subStatusId !== this.draggedConversation.subStatusId) {
      const previousSubStatus = this.draggedConversation.subStatus?.key;
      const originalSubStatusId = this.draggedConversation.subStatusId;
      const originalSubStatus = this.draggedConversation.subStatus;

      this.draggedConversation.subStatusId = subStatusId;
      this.draggedConversation.subStatus = subStatus;

      this.convoLists.forEach((list) => {
        if (list.viewTag === previousSubStatus) {
          list.removeConversation(this.draggedConversation);
        } else if (list.viewTag === newSubStatus) {
          list.addConversation(this.draggedConversation);
        }
      });

      this.subStatusKeyCount[newSubStatus]++;
      this.subStatusKeyCount[previousSubStatus]--;

      try {
        await this.conversationsService.update(this.draggedConversation.id, { subStatusId: subStatusId });
      } catch (error) {
        // Revert all the local changes
        this.draggedConversation.subStatusId = originalSubStatusId;
        this.draggedConversation.subStatus = originalSubStatus;

        // Revert UI changes
        this.convoLists.forEach((list) => {
          if (list.viewTag === newSubStatus) {
            list.removeConversation(this.draggedConversation);
          } else if (list.viewTag === previousSubStatus) {
            list.addConversation(this.draggedConversation);
          }
        });

        this.subStatusKeyCount[newSubStatus]--;
        this.subStatusKeyCount[previousSubStatus]++;

        this.msgService.showError(error);
      }
    }

    this.dragHighlightedCol = undefined;
  }

  onDragOver(event: DragEvent, status: string): void {
    event.preventDefault();
    this.dragHighlightedCol = status;
  }

  onDragOut(event: DragEvent, status: string): void {
    event.preventDefault();
    if (this.dragHighlightedCol === status) {
      this.dragHighlightedCol = undefined;
    }
  }

  async loadCollaboratorTypes() {
    const collaboratorTypes = await this.collaboratorTypesService.getCachedCollaboratorTypes();

    this.collaboratorTypes = collaboratorTypes;

    for (const collaboratorType of this.collaboratorTypes) {
      this.filterOptions.push({
        label: collaboratorType.name,
        searchId: 'assignedToId',
        id: `collaboratorKeyMap.${collaboratorType.key}`,
        icon: 'person',
        type: 'select',
        searchablePopoverOptions: {
          showNoneOption: true,
          noneOptionLabel: 'Unassigned',
        },
      } as any);
    }
  }

  async fetchConversation(conversationId: string) {
    return await this.conversationsService.get(conversationId, this.select);
  }

  async goToNextConversation() {
    if (this.selectedSidebarConversationIndex < this.sidebarConvoCount - 1) {
      this.selectedSidebarConversationIndex++;
      this.sidebarConvoList.manuallyRouteToConversationAtIndex(this.selectedSidebarConversationIndex);
    }
  }

  async goToPreviousConversation() {
    if (this.selectedSidebarConversationIndex > 0) {
      this.selectedSidebarConversationIndex--;
      this.sidebarConvoList.manuallyRouteToConversationAtIndex(this.selectedSidebarConversationIndex);
    }
  }

  isKanbanColumnVisible(columnKey: string): boolean {
    const statusFilterOption = this.filterOptions.find((f) => f.id === 'status');
    const subStatusFilterOption = this.filterOptions.find((f) => f.id === 'subStatus.key');
    const columnSubStatus = this.subStatuses?.find((s) => s.key === columnKey);

    if (!columnSubStatus) {
      return false; // Column key doesn't match a known sub-status
    }

    const columnType = columnSubStatus.type;
    interface FilterInfo {
      operator: FilterOperator;
      values: string[];
    }

    const typeFilters: FilterInfo[] = [];
    const subStatusFilters: FilterInfo[] = [];

    // 1. Separate filters into groups
    this.filters.forEach((filterValues, key) => {
      if (key.id === statusFilterOption?.id || key.id === subStatusFilterOption?.id) {
        const operator = key.operator ?? FILTER_OPERATORS.EQUALS; // Default to eq
        const values = filterValues.map((v) => v.id).filter((id): id is string => typeof id === 'string');

        if (values.length > 0) {
          const filterInfo: FilterInfo = { operator, values };

          if (key.id === statusFilterOption?.id) {
            typeFilters.push(filterInfo);
          } else {
            subStatusFilters.push(filterInfo);
          }
        }
      }
    });

    // 2. Check against 'Status Type' filters
    let allowedByType = true; // Assume allowed unless a filter restricts it
    const inclusiveTypeFilters = typeFilters.filter((f) => f.operator === FILTER_OPERATORS.EQUALS);
    const exclusiveTypeFilters = typeFilters.filter((f) => f.operator === FILTER_OPERATORS.NOT_EQUALS);

    if (inclusiveTypeFilters.length > 0) {
      allowedByType = inclusiveTypeFilters.some((f) => f.values.includes(columnType));
    }
    if (allowedByType && exclusiveTypeFilters.length > 0) {
      if (exclusiveTypeFilters.some((f) => f.values.includes(columnType))) {
        allowedByType = false;
      }
    }

    // 3. Check against 'Status' (subStatus.key) filters
    let allowedBySubStatus = true; // Assume allowed unless a filter restricts it
    const inclusiveSubStatusFilters = subStatusFilters.filter((f) => f.operator === FILTER_OPERATORS.EQUALS);
    const exclusiveSubStatusFilters = subStatusFilters.filter((f) => f.operator === FILTER_OPERATORS.NOT_EQUALS);

    if (inclusiveSubStatusFilters.length > 0) {
      allowedBySubStatus = inclusiveSubStatusFilters.some((f) => f.values.includes(columnKey));
    }
    if (allowedBySubStatus && exclusiveSubStatusFilters.length > 0) {
      if (exclusiveSubStatusFilters.some((f) => f.values.includes(columnKey))) {
        allowedBySubStatus = false;
      }
    }

    // 4. Combine results
    return allowedByType && allowedBySubStatus;
  }

  // Column management methods
  private getDefaultTableColumns(): TableColumn[] {
    return [
      { id: 'status', name: 'Status', visible: true, required: true, minWidth: 84, maxWidth: 84 },
      { id: 'message', name: 'Message', visible: true, required: true, minWidth: 520 },
      { id: 'submitter', name: 'Submitter', visible: true, minWidth: 180 },
      {
        id: 'customer',
        name: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
        visible: true,
        minWidth: 180,
      },
      { id: 'assignee', name: 'Assignee', visible: true, minWidth: 180 },
      { id: 'ticketType', name: 'Ticket Type', visible: true, minWidth: 180 },
      {
        id: 'account',
        name: 'Account',
        visible: this.authService.tenant.accountsEnabled,
        minWidth: 180,
      },
      { id: 'submitted', name: 'Submitted', visible: true, minWidth: 170, maxWidth: 170 },
      { id: 'tags', name: 'Tags', visible: true, minWidth: 250 },
      {
        id: 'customerTags',
        name: this.authService.tenant.accountsEnabled ? 'Channel Tags' : 'Customer Tags',
        visible: true,
        minWidth: 250,
      },
      { id: 'responseTime', name: 'Response time', visible: false, minWidth: 170, maxWidth: 170 },
      { id: 'resolutionTime', name: 'Resolution time', visible: false, minWidth: 170, maxWidth: 170 },
      { id: 'project', name: 'Project', visible: false, minWidth: 180 },
    ];
  }

  private loadTableColumnsFromStorage(): void {
    const saved = localStorage.getItem('inboxTableColumns');
    if (saved) {
      try {
        this.tableColumns = JSON.parse(saved);
        // Ensure we have all columns (in case new ones were added)
        const defaultColumns = this.getDefaultTableColumns();
        const savedColumnIds = this.tableColumns.map((col) => col.id);

        // Add any missing columns
        defaultColumns.forEach((defaultCol) => {
          if (!savedColumnIds.includes(defaultCol.id)) {
            this.tableColumns.push(defaultCol);
          }
        });

        // Remove any columns that no longer exist
        this.tableColumns = this.tableColumns.filter((col) =>
          defaultColumns.some((defaultCol) => defaultCol.id === col.id),
        );
      } catch (error) {
        console.warn('Failed to parse saved table columns, using defaults:', error);
        this.tableColumns = this.getDefaultTableColumns();
      }
    } else {
      this.tableColumns = this.getDefaultTableColumns();
    }
  }

  private saveTableColumnsToStorage(): void {
    localStorage.setItem('inboxTableColumns', JSON.stringify(this.tableColumns));
  }

  async showColumnManager(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: ColumnManagerComponent,
      componentProps: {
        columns: this.tableColumns,
        onSave: (columns: TableColumn[]) => {
          this.tableColumns = columns;
          this.saveTableColumnsToStorage();
          this.analyticsService.trackEvent('Inbox', 'Updated Table Columns');
        },
      },
    });

    await modal.present();
  }

  isColumnVisible(columnId: string): boolean {
    const column = this.tableColumns.find((col) => col.id === columnId);
    return column ? column.visible : true;
  }

  getColumnWidth(columnId: string): { minWidth?: number; maxWidth?: number } {
    const column = this.tableColumns.find((col) => col.id === columnId);
    if (!column) return {};

    const result: { minWidth?: number; maxWidth?: number } = {};
    if (column.minWidth) result.minWidth = column.minWidth;
    if (column.maxWidth) result.maxWidth = column.maxWidth;
    return result;
  }

  getVisibleColumnsInOrder(): TableColumn[] {
    return this.tableColumns.filter((col) => col.visible);
  }

  getColumnDisplayName(columnId: string): string {
    const column = this.tableColumns.find((col) => col.id === columnId);
    if (!column) return '';

    // Handle dynamic names based on tenant settings
    switch (columnId) {
      case 'customer':
        return this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer';
      case 'customerTags':
        return this.authService.tenant.accountsEnabled ? 'Channel Tags' : 'Customer Tags';
      default:
        return column.name;
    }
  }

  // Escalation-specific column management methods
  isEscalationColumnVisible(columnId: string): boolean {
    // All escalation columns are always visible
    return true;
  }

  getEscalationColumnWidth(columnId: string): { minWidth?: number; maxWidth?: number } {
    // Hardcoded column widths for escalations
    const widths: Record<string, { minWidth?: number; maxWidth?: number }> = {
      status: { minWidth: 134, maxWidth: 134 },
      title: { minWidth: 300 },
      escalationType: { minWidth: 180 },
      ticketType: { minWidth: 180 },
      priority: { minWidth: 120, maxWidth: 120 },
      assignee: { minWidth: 180 },
      createdAt: { minWidth: 170, maxWidth: 170 },
      updatedAt: { minWidth: 170, maxWidth: 170 },
      acknowledgedAt: { minWidth: 170, maxWidth: 170 },
      resolvedAt: { minWidth: 170, maxWidth: 170 },
      conversation: { minWidth: 120 },
      slackChannel: { minWidth: 180 },
    };
    return widths[columnId] || {};
  }

  getVisibleEscalationColumnsInOrder(): { id: string; name: string }[] {
    // Hardcoded escalation columns in order
    return [
      { id: 'status', name: 'Status' },
      { id: 'title', name: 'Title' },
      { id: 'escalationType', name: 'Escalation Type' },
      { id: 'ticketType', name: 'Ticket Type' },
      { id: 'priority', name: 'Priority' },
      { id: 'assignee', name: 'Assignee' },
      { id: 'createdAt', name: 'Created' },
      { id: 'updatedAt', name: 'Updated' },
      { id: 'conversation', name: 'Conversation' },
    ];
  }

  getEscalationColumnDisplayName(columnId: string): string {
    const columns = this.getVisibleEscalationColumnsInOrder();
    const column = columns.find((col) => col.id === columnId);
    return column ? column.name : '';
  }

  toggleEscalationsFilter() {
    this.isFilteringEscalationsByMe = !this.isFilteringEscalationsByMe;
    this.loadConversations();
  }
}
